// [claude-code 2026-05-16] S67: Agentic Scoring Breakdown rename and compact header behavior.
// [Codex 2026-07-14] S138: body-click econ drawer remains independent from More Info.
import { useState } from "react";
import { useMarketAwarenessDossier } from "../hooks/useMarketAwarenessDossier";
import { colorForScore } from "../lib/fuse-palette";
import {
  formatMarketPriceActionLine,
  normalizeMarketAwarenessSymbol,
} from "../lib/market-price-action-reference";
import type { IVScoreResponse } from "../types/market-data";
import type { EconHeaderCountdownEvent } from "../types/econ-header";
import {
  IVScoreDesktopChip,
  IVScoreLoadingChip,
  IVScoreMobileChip,
} from "./iv-score/IVScoreChips";
import {
  capLightStyle,
  environmentLabel,
  installIvScoreKeyframes,
} from "./iv-score/iv-score-visuals";
import { useIvScoreMotion } from "./iv-score/use-iv-score-motion";
import { useIvScoreTooltip } from "./iv-score/use-iv-score-tooltip";

interface IVScoreCardProps {
  data: IVScoreResponse | null;
  loading?: boolean;
  layoutOption?: "tickers-only" | "combined";
  compactCopy?: boolean;
  mobileCombined?: boolean;
  latestReleasedEconEvent?: EconHeaderCountdownEvent | null;
}

installIvScoreKeyframes();

export function IVScoreCard({
  data,
  loading,
  layoutOption: _layoutOption,
  compactCopy = false,
  mobileCombined = false,
  latestReleasedEconEvent,
}: IVScoreCardProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltip = useIvScoreTooltip(showTooltip && !mobileCombined);
  const motion = useIvScoreMotion(data);
  const marketInstrument = normalizeMarketAwarenessSymbol(
    data?.marketPerformance?.instrument ?? data?.instrument ?? "/NQ",
  );
  const { dossier: liveMarketAwareness } = useMarketAwarenessDossier(
    marketInstrument,
    {
      enabled: Boolean(data?.marketPerformance),
      initialDossier: data?.marketPerformance?.marketAwareness ?? null,
    },
  );

  if (loading || !data) {
    return (
      <IVScoreLoadingChip
        compactCopy={compactCopy}
        mobileCombined={mobileCombined}
        latestReleasedEconEvent={latestReleasedEconEvent}
      />
    );
  }

  const scoreColor = colorForScore(data.score);
  if (mobileCombined) {
    return (
      <IVScoreMobileChip
        capLightStyle={motion.capLight}
        data={data}
        flash={motion.flash}
        scoreColor={scoreColor}
        valueStyle={motion.valueStyle}
        latestReleasedEconEvent={latestReleasedEconEvent}
      />
    );
  }

  const marketLine = data.marketPerformance
    ? formatMarketPriceActionLine(
        liveMarketAwareness ?? data.marketPerformance.marketAwareness ?? null,
        marketInstrument,
      )
    : null;
  const marketDetail = marketLine
    ? `${marketLine.instrument} ${marketLine.price}; ${marketLine.move}; ${marketLine.vwap}`
    : (data.marketPerformance?.detail ?? "");
  const openInfo = () => {
    tooltip.show();
    setShowTooltip(true);
  };
  const closeInfo = () => tooltip.queueHide(() => setShowTooltip(false));

  return (
    <IVScoreDesktopChip
      compactCopy={compactCopy}
      data={data}
      environment={environmentLabel(data.score)}
      marketDetail={marketDetail}
      onInfoEnter={openInfo}
      onInfoLeave={closeInfo}
      popupPosition={tooltip.position}
      scoreColor={scoreColor}
      showTooltip={showTooltip}
      style={capLightStyle(scoreColor)}
      triggerRef={tooltip.triggerRef}
      latestReleasedEconEvent={latestReleasedEconEvent}
    />
  );
}
